import { z } from 'zod';
import { extensionSecuritySettingsSchema } from './extension-security';
import { severitySchema } from './shared/severity';
import { osvComponentSchema } from './shared/osv';
export const analysisTypeSchema = z.enum(['extensions']);
/*
  CapabilitySeverity is used exclusively for ExtensionCapability metadata.
  Capabilities are informational signals that do not raise the risk level.
*/
export const capabilitySeveritySchema = z.enum(['info', 'low']);
/*
  ExtensionFindingType defines the available extension finding types.
*/
export var ExtensionFindingType;
(function (ExtensionFindingType) {
    ExtensionFindingType["ExcessivePermissions"] = "excessive_permissions";
    /**
     * Org-authored custom capability rule (KIR-2691 §7). Distinct from
     * `ExcessivePermissions` so the operator's authored `reason` renders verbatim
     * in the alert body: custom rules route by severity directly and are kept out
     * of the weighted/threshold model, so they must NOT inherit the static
     * `dangerousPermissions.excessive_permissions` "risk threshold" copy. It has
     * no `TYPE_DISPLAY` entry on purpose — the alert helpers fall back to the
     * authored reason for unmapped `(subPolicyKey, type)` pairs.
     */
    ExtensionFindingType["CustomCapabilityRule"] = "custom_capability_rule";
    ExtensionFindingType["UnverifiedPublisher"] = "unverified_publisher";
    ExtensionFindingType["MaliciousExtension"] = "malicious_extension";
    ExtensionFindingType["SuspiciousExtensionName"] = "suspicious_extension_name";
    ExtensionFindingType["Typosquatting"] = "typosquatting";
    ExtensionFindingType["HomographAttack"] = "homograph_attack";
    ExtensionFindingType["MixedScriptAttack"] = "mixed_script_attack";
    ExtensionFindingType["OSVVulnerability"] = "osv_vulnerability";
})(ExtensionFindingType || (ExtensionFindingType = {}));
export const extensionFindingTypeSchema = z.enum(ExtensionFindingType);
/**
 * One weighted-scoring contributor that fed an aggregate (or instant) finding.
 * Persisted on `findings.custom_attributes` and surfaced as alert
 * `scoreBreakdown.contributors` (KIR-3781).
 */
export const scoreContributorSchema = z.object({
    field: z.string(),
    weight: z.number().int().min(0),
    ruleId: z.string(),
    correlationGroup: z.string().optional(),
});
export const extensionFindingSchema = z.object({
    type: extensionFindingTypeSchema,
    extensionId: z.string(),
    extensionName: z.string().optional(),
    publisher: z.string().optional(),
    reason: z.string(),
    severity: severitySchema,
    category: z.string().optional(),
    permissions: z.array(z.string()).optional(),
    /**
     * Aggregate weighted score that produced this finding (KIR-2691).
     * Present on built-in catalog score findings; absent on instant-signal,
     * custom-rule, and non-catalog findings.
     */
    score: z.number().int().min(0).optional(),
    /**
     * Manifest fields of the capabilities that combined to produce this finding
     * (KIR-2691). Derived from `scoreContributors[].field` (stable sort) for
     * backward compatibility — prefer `scoreContributors` for weight/rule detail.
     */
    matchedCapabilities: z.array(z.string()).optional(),
    /**
     * Weighted score contributors (KIR-3781). Present on built-in catalog
     * aggregate findings and optionally on instant-signal findings (single
     * contributor, no aggregate `score`). Absent on custom-rule findings.
     */
    scoreContributors: z.array(scoreContributorSchema).optional(),
    osvId: z.string().optional(),
    /** KIR-4190: see `osvComponentSchema`. */
    osvComponent: osvComponentSchema.optional(),
    osvDetails: z.string().optional(),
    osvIOCs: z
        .object({
        urls: z.array(z.string()).optional(),
        ips: z.array(z.string()).optional(),
    })
        .optional(),
});
/*
  ExtensionCapability represents an informational capability detected in an extension manifest.
  Capabilities are NOT security findings — they provide visibility into what an extension can do
  without raising the risk level or triggering alerts.
*/
export const extensionCapabilitySchema = z.object({
    ruleId: z.string(),
    category: z.string(),
    field: z.string(),
    matchedValue: z.unknown().optional(),
    description: z.string(),
    severity: capabilitySeveritySchema,
    extensionId: z.string(),
    extensionName: z.string().optional(),
    publisher: z.string().optional(),
});
export const extensionScanResultSchema = z.object({
    total_findings: z.number().int().min(0),
    findings: z.array(extensionFindingSchema),
    capabilities: z.array(extensionCapabilitySchema).default([]),
    has_issues: z.boolean(),
    critical_count: z.number().int().min(0),
    high_count: z.number().int().min(0),
    medium_count: z.number().int().min(0),
    low_count: z.number().int().min(0),
    scanned_extensions: z.number().int().min(0),
});
export const extensionAnalysisRequestSchema = z.object({
    content: z.string(),
    settings: extensionSecuritySettingsSchema.optional(),
});
export const extensionAnalysisResponseSchema = extensionScanResultSchema;
export const analysisRequestSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('extensions'),
        payload: extensionAnalysisRequestSchema,
    }),
]);
export const analysisResponseSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('extensions'),
        result: extensionScanResultSchema,
    }),
]);
//# sourceMappingURL=analyses.js.map